/**
 * @file        worker-and-daemons.js
 * @author      Paul <paul@distributive.network>
 * @date        November 2023
 *
 * Support for the shared code of the interaction of worker with Soteria, TD and RS.
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
/* global dcpConfig */ // eslint-disable-line no-redeclare
// @ts-check
'use strict';

const { jobStatus } = require('dcp/common/scheduler-constants');
const debuggingSoteria = require('dcp/debugging').scope('soteria');
const debuggingWorker = require('dcp/debugging').scope('worker');
const debugging = () => {
  return debuggingSoteria() || debuggingWorker();
}

/**
 * Helpers for communicating jobPerfData stats with Supervisor, RS, TD.
 *  collectMetrics
 *  constructAlpha
 *  constructAlphaFromDb
 *  localElapsedVariance
 *  globalElapsedTime
 *  globalElapsedVariance
 *  globalCPUDensity
 *  globalGPUDensity
 *  nextEma
 *  nextEmvar
 *********************************************************************************************/

/**
 * Construct the payload for updating job metrics in Supervisor.
 * Returned as part of getJobsForTask and in result submitter 'result' processing.
 * @param {*} job         - Row from jobs table.
 * @param {*} jobPerfData - Correpsonding row from jobPerfData table.
 * @returns {{sliceCPUTime: number, sliceGPUTime: number, sliceCPUDensity: number, sliceGPUDensity: number, lastSliceNumber: number, measuredSlices: number, alpha: number}}
 */
exports.collectMetrics = function utils$$collectMetrics(job, jobPerfData)
{
  const lastSliceNumber = Number(job.lastSliceNumber || 0);
  const nextSliceNumber = Number(job.nextSliceNumber || 0);
  // Invariant jobPerfData.measuredSlices <= min(job.nextSliceNumber-1,  job.lastSliceNumber)
  const measuredSlices = Math.min(Number(jobPerfData.measuredSlices), nextSliceNumber, lastSliceNumber);
  const alpha = exports.constructAlpha(lastSliceNumber, measuredSlices);
  const metrics = {
    sliceCPUTime:     Number(jobPerfData.sliceCPUTime || 0),
    sliceGPUTime:     Number(jobPerfData.sliceGPUTime || 0),
    sliceCPUDensity:  Number(jobPerfData.sliceCPUDensity || 0),
    sliceGPUDensity:  Number(jobPerfData.sliceGPUDensity || 0),
    sliceInDataSize:  Number(jobPerfData.sliceInDataSize || 0),
    sliceOutDataSize: Number(jobPerfData.sliceOutDataSize || 0),
    // Transmit the EMA coefficient information to supervisor.
    lastSliceNumber,
    measuredSlices,
    alpha,
    running: job.status === jobStatus.running,
  };
  return metrics;
}

/**
 * Construct the EMA coefficient for computing jobPerfData columns.
 * @param {number} lastSliceNumber - job.lastSliceNumber
 * @param {number} measuredSlices  - Math.min(jobPerfData.measuredSlices, job.nextSliceNumber, job.lastSliceNumber)
 * @returns {number}               - 0 < retVal <= 0.5
 */
exports.constructAlpha = function utils$$constructAlpha(lastSliceNumber, measuredSlices)
{
  // lastSliceNumber - measuredSlices is # of slices which have not reported results.
  const alpha = Math.min(0.5, Math.max(1.0, lastSliceNumber - measuredSlices) / lastSliceNumber);
  return alpha;
}

/**
 * Construct the EMA coefficient for computing jobPerfData columns.
 * @param {*} job          - row in jobs table
 * @param {*} jobPerfData  - corresponding row in jobPerfData table
 * @returns {number}       - 0 < retVal <= 0.5
 */
exports.constructAlphaFromDb = function utils$$constructAlpha(job, jobPerfData)
{
  const last = Number(job.lastSliceNumber || 0);
  const next = Number(job.nextSliceNumber || 1);
  const measured = Math.min(Number(jobPerfData.measuredSlices), next, last);
  const alpha = exports.constructAlpha(last, measured);
  return alpha;
}

/**
 * An explanation for the calculations below.
 * 
 * Local means the EMAs measured on a single worker, whereas global means the EMAs in table 'jobPerfData'.
 * 
 * The sandbox timing code returns { total, CPU, webGPU, webGL } where total is wall-clock time,
 * which is transformed and sent to RS, has:
 *   GPU = webGPU + webGL
 *   CPUTime = CPU
 *   CPUDensity = CPU / total
 *   GPUTime = GPU
 *   CPUDensity = GPU / total
 * 
 * The local elapsed (viz., wall-clock) time is
 *    wall-clock-time = CPUTime / CPUDensity because CPUDensity = wall-clock-time / CPUTime.
 * Analogously, global elapsed time is defined to be sliceCPUTime / sliceCPUDensity.
 * We can also express this equivalently as
 *    local-wall-clock-time = (CPUTime + GPUTime) / (CPUDensity + GPUDensity)
 *    global-wall-clock-time = (sliceCPUTime + sliceGPUTime) / (sliceCPUDensity + sliceGPUDensity)
 * What is the variance of wall-clock-time ( written Var(wall-clock-time) ) ?
 * While that can be computed directly on a worker, there isn't sufficient information to do it
 * globally so we make the following simplifying assumption
 *    Var(sliceCPUTime + sliceGPUTime) = 0
 * Then since for a constant C we have
 *    Var(X / C) = Var(X) / C^2
 * and hence
 *    Var(global-wall-clock-time) = Var(sliceCPUTime + sliceGPUTime) / (sliceCPUDensity + sliceGPUDensity)^2
 * which is exactly the formula used for for localElapsedDensity and globalElapsedDensity.
 * ------------------------------------------------------------------------------------------------------------
 * In summary, I use (sliceCPUTime + sliceGPUTime) / (sliceCPUDensity + sliceGPUDensity) instead
 * of sliceCPUTime / CPUDensity even though they are equivalent, because the former allows
 * computation of Var(global-wall-clock-time) with a reasonable simplifying assumption,
 * whereas the latter does not allow a Var calculation at all.
 */

/**
 * Compute the variance of the total amount of time it takes to execute a slice. (will be ok)
 *   Var(X/C)= Var(X)/(C * C)
 * 
 * Wall-clock-time = (CPUTime + GPUTime) / (CPUDensity + GPUDensity)
 * By assuming Var(CPUDensity + GPUDensity) = 0 we have
 * Var(wall-clock-time) = Var(CPUTime + GPUTime) / (CPUDensity + GPUDensity)^2
 * 
 * @param {{CPUTime_variance: number, GPUTime_variance: number, CPUDensity: number, GPUDensity: number}} metrics
 * @returns {number}
 */
exports.localElapsedVariance = function utils$$localElapsedVariance (metrics)
{
  if (!metrics) return 0;
  const totalDensity = (metrics.CPUDensity || 0) + (metrics.GPUDensity || 0);
  const lev = ((metrics.CPUTime_variance || 0) + (metrics.GPUTime_variance || 0)) / (totalDensity * totalDensity || 1);
  return lev;
}

/**
 * Compute total amount of time it takes to execute a slice.
 * 
 * On a worker wall-clock-time = CPUTime / CPUDensity because CPUDensity = wall-clock-time / CPUTime.
 * Observe that CPUTime / CPUDensity = (CPUTime + GPUTime) / (CPUDensity + GPUDensity) because
 * GPUDensity is also equal to wall-clock-time / GPUTime.  Therefore we have 2 ways to express
 * wall-clock time. For the EMAs computed across all workers for a given job (viz., a row in
 * table 'jobPerfData'), we use the analogous formulas.
 * 
 * @param {{sliceCPUTime: number, sliceGPUTime: number, sliceCPUDensity: number, sliceGPUDensity: number}} metrics
 * @returns {number}
 */
exports.globalElapsedTime = function utils$$globalElapsedTime(metrics)
{
  if (!metrics) return 0;
  const getime = (metrics.sliceCPUTime || 0) / (metrics.sliceCPUDensity || 1);
  return getime;
}

/**
 * Compute the variance of the total amount of time it takes to execute a slice.
 *   Var(X/C) = Var(X)/(C * C)
 * 
 * Wall-clock-time = (sliceCPUTime + sliceGPUTime) / (sliceCPUDensity + sliceGPUDensity)
 * By assuming Var(sliceCPUDensity + sliceGPUDensity) = 0 we have
 * Var(wall-clock-time) = Var(sliceCPUTime + sliceGPUTime) / (sliceCPUDensity + sliceGPUDensity)^2
 * 
 * @param {{sliceCPUTime_variance: number, sliceGPUTime_variance: number, sliceCPUDensity: number, sliceGPUDensity: number}} metrics
 * @returns {number}
 */
exports.globalElapsedVariance = function utils$$globalElapsedVariance (metrics)
{
  if (!metrics) return 0;
  const totalDensity = (metrics.sliceCPUDensity || 0) + (metrics.sliceGPUDensity || 0);
  const gev = ((metrics.sliceCPUTime_variance || 0) + (metrics.sliceGPUTime_variance || 0)) / (totalDensity * totalDensity || 1);
  return gev;
}

/**
 * Compute CPU density.
 * @param {{sliceCPUTime: number, sliceGPUTime: number, sliceCPUDensity: number, sliceGPUDensity: number}} metrics
 * @returns {number}
 */
exports.globalCPUDensity = function utils$$globalCPUDensity(metrics)
{
  const gcdens = metrics?.sliceCPUDensity || 1;
  return gcdens;
}

/**
 * Compute GPU density.
 * Only use globalGPUDensity in the context of a GPU job.
 * @param {{sliceCPUTime: number, sliceGPUTime: number, sliceCPUDensity: number, sliceGPUDensity: number}} metrics
 * @returns {number}
 */
exports.globalGPUDensity = function utils$$globalGPUDensity(metrics)
{
  const ggdens = metrics?.sliceGPUDensity || 1;
  return ggdens;
}

/**
 * Create next step in an EMA time series of numeric values.
 *   return alpha * current + (1 - alpha) * previous;
 * @param {number} previous
 * @param {number} current
 * @param {number} alpha
 * @returns {number}
 */
exports.nextEma = function utils$$nextEma(previous, current, alpha)
{
  if (!previous)
    return current;
  return alpha * current + (1 - alpha) * previous;
}

/**
 * Calculate Exponential Moving Variance.
 *
 * How to create EMA and EMVAR series from x[i], i = 0, 1, 2, ...
 *   delta[i] <- x[i] - ema[i-1]
 *   ema[i] <- ema[i-1] + alpha * delta[i]: ema[0] = x[0]
 *   emvar[i] <- (1-alpha) * (emvar[i-1] + alpha * delta[i]^2): emvar[0] = 0
 *
 * @param {number|null} previous -- emvar[i-1]
 * @param {number} previousEma   -- ema[i-1]
 * @param {number} current       -- price[i]
 * @param {number} alpha
 * @returns {number}
 */
exports.nextEmvar = function utils$$nextEmvar(previous, previousEma, current, alpha)
{
  if (previous === null)
    return 0;
  return (1 - alpha) * (previous + alpha * (current - previousEma) * (current - previousEma));
}

/**
 * Access to dcpConfig hive shared between the Soteria daemon and the Worker.
 */
class SoteriaSharedHive
{
  /** @type {*} */
  #shared;
  /** @type {number} */
  #targetTaskDuration;

  /** @param {{ shared: *, targetTaskDuration: number }} [soteriaConfig] */
  constructor (soteriaConfig)
  {
    // When called in scheduler soteriaConfig is not needed.
    if (soteriaConfig)
    {
      this.#shared = soteriaConfig.shared;
      this.#targetTaskDuration = soteriaConfig.targetTaskDuration;
    }
    else
    {
      const baseHive = dcpConfig.scheduler.services;
      this.#shared = baseHive.soteria?.shared;
      this.#targetTaskDuration = baseHive.taskDistributor?.tuning?.targetTaskDuration;
    }
    if (false || debugging())
    {
      console.debug('SoteriaSharedHive:')
      console.debug('frequencyMs',         Number(this.#shared?.frequencyMs));
      console.debug('debuggingFactor',     Number(this.#shared?.debuggingFactor));
      console.debug('useLifetimeAnalysis', Boolean(this.#shared?.useLifetimeAnalysis));
      console.debug('lifetimeFactor',      Number(this.#shared?.lifetimeFactor));

      console.debug('maxCotasking',    Number(this.#shared?.slice?.maxCotasking));
      console.debug('sliceMultiplier', Number(this.#shared?.slice?.multiplier));
      console.debug('sliceSigmas',     Number(this.#shared?.slice?.sigmas));
      console.debug('sliceBiasMs',     Number(this.#shared?.slice?.biasMs));
      console.debug('sliceFactor',     Number(this.#shared?.slice?.factor));

      console.debug('targetTaskDuration', this.#targetTaskDuration);
      console.debug('------------------------------------------------------------------------');
      console.debug('frequencyMs',         this.frequencyMs);
      console.debug('debuggingFactor',     this.debuggingFactor);
      console.debug('useLifetimeAnalysis', this.useLifetimeAnalysis);
      console.debug('lifetimeFactor',      this.lifetimeFactor);

      console.debug('maxCotasking',    this.maxCotasking);
      console.debug('sliceMultiplier', this.sliceMultiplier);
      console.debug('sliceSigmas',     this.sliceSigmas);
      console.debug('sliceBiasMs',     this.sliceBiasMs);

      console.debug('targetTaskDurationMs', this.targetTaskDurationMs);
      console.debug('::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::');
    }
  }
  get frequencyMs ()         { return Number(this.#shared?.frequencyMs || 4 * 60 * 1000) * this.debuggingFactor; }
  get debuggingFactor ()     { return Number(this.#shared?.debuggingFactor || 1.0); }
  get useLifetimeAnalysis () { return Boolean(this.#shared?.useLifetimeAnalysis ?? false); }
  get lifetimeFactor ()      { return Number(this.#shared?.lifetimeFactor || 1.0); }

  get maxCotasking ()    { return Number(this.#shared?.slice?.maxCotasking ?? 3); }
  get sliceMultiplier () { return Number(this.#shared?.slice?.multiplier || 1.5); }
  get sliceSigmas ()     { return Number(this.#shared?.slice?.sigmas || 2); }
  get sliceBiasMs ()     { return Number(this.#shared?.slice?.biasMs || 2 * 60 * 1000) * this.debuggingFactor; }

  get targetTaskDurationMs () { return Number(this.#targetTaskDuration || 180) * 1000 * this.debuggingFactor; }

  get beaconMs () { return Math.max(Number(this.#shared?.slice?.factor || 1.2) * this.frequencyMs, this.targetTaskDurationMs); }
}
exports.SoteriaSharedHive = SoteriaSharedHive;
